import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppConfigService } from '@app/app-config.service';
import { LicenseService } from '@app/core/services/license/license.service';
import { DivisionService } from '@app/features/division/services/division.service';
import { Page } from '@app/shared/models/page.interface';
import { SaveResult } from '@app/shared/models/saveResult';
import { SearchParams } from '@app/shared/models/search-params.model';
import { Profile } from '@app/shared/models/user.interface';
import { DataTablePageState } from '@components/data/data-table-new/directives/datatable-pagination.directive';
import { DataTableSortState } from '@components/data/data-table-new/directives/datatable-sort.directive';
import { forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface ProfileDto {
  profile: Profile;
  deactivateAll?: boolean;
  assignTo?: string;
}

export interface ProfileActiveResponse {
  activeProfiles: Profile[];
  saveResult?: SaveResult;
}

@Injectable({
  providedIn: 'root'
})
export class ProfileService {
  constructor(
    protected http: HttpClient,
    protected licenseService: LicenseService,
    protected divisionService: DivisionService,
    protected appConfigService: AppConfigService
  ) {}

  getNew(): Observable<Profile> {
    // FIXME: When we have more than one license, we will need to get the proper one (like in users)
    return forkJoin(this.licenseService.getByName('Default'), this.divisionService.getAll()).pipe(
      map(result => {
        let profile: Profile = { active: true, profileLicenses: [], profileDivisions: [] } as Profile;
        //profile.profileLicenses.push({profile: profile, license: result[0]});
        profile.profileLicenses = [];
        if (result[1].length === 1) {
          profile.profileDivisions.push({ id: null, profile: null, division: result[1][0] });
        }
        return profile;
      })
    );
  }

  /**
   * Get all Profiles
   */
  getAll(b2b: boolean = false): Observable<Profile[]> {
    let params = new HttpParams();
    params = params.append('b2b', b2b.toString());
    return this.http.get<Profile[]>(`${this.appConfigService.getApi()}/profiles/all`, { params });
  }

  search(
    pageState: DataTablePageState,
    sortState: DataTableSortState,
    search: string,
    filter: string
  ): Observable<Page<Profile>> {
    const params: SearchParams = {
      search,
      filter,
      pageNumber: pageState.index,
      pageSize: pageState.size,
      sortDirection: sortState.direction.toString().toUpperCase(),
      sortField: sortState.column
    };
    return this.http.post<Page<Profile>>(`${this.appConfigService.getApi()}/profiles/search`, params);
  }

  /** @deprecated use search() method instead */
  getAllPaginated(
    endpoint: string,
    page: DataTablePageState,
    sort: DataTableSortState,
    filter: string
  ): Observable<Page<Profile>> {
    let params = new HttpParams();
    params = params.append('filter', filter);
    params = params.append('page', page.index.toString());
    params = params.append('size', page.size.toString());
    params = params.append('sort', sort.column + ',' + sort.direction);

    return this.http.post<Page<Profile>>(this.appConfigService.getApi() + endpoint, {
      params
    });
  }

  getActiveProfiles(b2b: boolean = false): Observable<Profile[]> {
    let params = new HttpParams();
    params = params.append('b2b', b2b.toString());
    return this.http.get<Profile[]>(`${this.appConfigService.getApi()}/profiles/active`, { params });
  }

  getById(id: string): Observable<Profile> {
    return this.http.get<Profile>(`${this.appConfigService.getApi()}/profiles/${id}`);
  }

  save(dto: ProfileDto): Observable<SaveResult> {
    return this.http.post<SaveResult>(`${this.appConfigService.getApi()}/profiles`, dto);
  }

  changeState(selection: string[], active: boolean, assignTo: string = null): Observable<ProfileActiveResponse> {
    return this.http.post<ProfileActiveResponse>(`${this.appConfigService.getApi()}/profiles/active/`, {
      ids: selection,
      active,
      profileToDumpUsers: assignTo
    });
  }
}
